import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from '@tanstack/react-router'

type ButtonVariant = 'outline' | 'solid' | 'inverse' | 'ghost'

interface BaseButtonProps {
  children: ReactNode
  variant?: ButtonVariant
  className?: string
}

export interface ButtonLinkProps
  extends BaseButtonProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> {
  href: string
}

export interface ButtonProps
  extends BaseButtonProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {}

const variantClass: Record<ButtonVariant, string> = {
  outline: 'ag-button ag-button-outline',
  solid: 'ag-button ag-button-solid',
  inverse: 'ag-button ag-button-inverse',
  ghost: 'ag-button ag-button-ghost',
}

function isInternalHref(href: string) {
  return href.startsWith('/') && !href.startsWith('//')
}

function internalLinkProps(href: string) {
  const [path, query] = href.split('?')
  if (!query) return { to: path }
  const params = Object.fromEntries(new URLSearchParams(query).entries())
  return { to: path, search: params }
}

export function ButtonLink({
  children,
  variant = 'outline',
  className = '',
  href,
  ...props
}: Readonly<ButtonLinkProps>) {
  const classes = `${variantClass[variant]} ${className}`
  if (isInternalHref(href)) {
    return (
      <Link className={classes} {...internalLinkProps(href)} {...props}>
        {children}
      </Link>
    )
  }
  return (
    <a className={classes} href={href} {...props}>
      {children}
    </a>
  )
}

export function Button({
  children,
  variant = 'outline',
  className = '',
  ...props
}: Readonly<ButtonProps>) {
  return (
    <button className={`${variantClass[variant]} ${className}`} {...props}>
      {children}
    </button>
  )
}
